import React, { useEffect, useRef, useState } from 'react';
import { IonToolbar, IonButtons, IonIcon } from '@ionic/react';
import { OrderByEnum } from 'common/common';
import { useSelector } from 'react-redux';
import { IState } from 'appRedux/createStore';
import { Insights } from 'config/Insights';
import { timeframes } from 'config/config';
import { closeOutline, optionsOutline } from 'ionicons/icons';
import TimeFrameInline from './CompareAdvancedView/TimeFrameInline';
import InsightInline from './CompareAdvancedView/InsightInline';
import {
  getUniversalSearchExploreLabel,
  subscribeUniversalSearchExploreLabel,
  universalSearchExploreSubtitle,
} from 'lib/universalSearchExploreLabel';

const ResultsToolbar: React.FC<{
  totalReportCount: number;
  gameGroupName: string;
  isBankFilter: boolean;
  insightKey: string;
  orderBy: OrderByEnum;
  timeframeKey: string;
  onFiltersToggle?: (open: boolean) => void;
  onHeaderDoubleTap?: () => void;
  /** When false, insight + timeframe rows collapse (title stays). */
  filtersVisible?: boolean;
  /**
   * When false, fade out title + insight/timeframe (EGM loading).
   * Opacity + pointer-events; panels stay mounted. Default true.
   */
  headerChromeVisible?: boolean;
  /**
   * Loading collapse (display-only): while true the whole header block (title +
   * Insight + Timeframe) slides up, fades and collapses its height; false slides it
   * back in. Separate from `filtersVisible` (the scroll auto-hide) so the two never
   * fight: both can be set at once and each releases on its own.
   */
  sectionsCollapsed?: boolean;
}> = ({
  totalReportCount,
  gameGroupName,
  isBankFilter,
  insightKey,
  orderBy,
  timeframeKey,
  onFiltersToggle,
  onHeaderDoubleTap,
  filtersVisible = true,
  headerChromeVisible = true,
  sectionsCollapsed = false,
}) => {
  const orderLabel = orderBy === OrderByEnum.Desc ? 'desc' : 'asc';
  const groupGameName = gameGroupName?.trim();
  const isSearchMode = useSelector<IState>(
    (state) => state.app.persistedState.slotState.isSearchMode
  ) as boolean;
  const groupName = isBankFilter || isSearchMode ? '' : groupGameName;
  // "<game> · from Slot Check Search" while the Slot Check Search filter is in view (mock 06).
  const selectedSlotsName = useSelector<IState>(
    (state) => state.app.persistedState.slotState.selectedSlotsName
  ) as string | null;
  const viewCasinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.casinoId
  ) as number | undefined;
  const [usLabel, setUsLabel] = useState(getUniversalSearchExploreLabel());
  useEffect(() => subscribeUniversalSearchExploreLabel(() => setUsLabel(getUniversalSearchExploreLabel())), []);
  const fromSearchSubtitle = universalSearchExploreSubtitle(usLabel, {
    casinoId: viewCasinoId,
    isSearchMode,
    selectedSlotsName,
  });
  const groupPlural = groupName ? (groupName.endsWith('s') ? groupName : `${groupName}s`) : '';
  const insightLabel = Insights.find((eachInsight) => eachInsight.value === insightKey)?.short_name;
  const timeframeLabel = timeframes.find(
    (timeFrame) => timeFrame.value === timeframeKey
  )?.description;
  const insightTimeframeLabel = [insightLabel, timeframeLabel].filter(Boolean).join(' ');
  const [showFilters, setShowFilters] = useState(false);

  // Double-tap on the drawer header fast-scrolls the EGM list to the first
  // record. Ignore taps that land on the interactive filter controls (insight /
  // timeframe chips, the insights toggle) so selecting a filter twice quickly
  // doesn't also trigger the scroll-to-top.
  const lastHeaderTapRef = useRef(0);
  const handleHeaderTap = (e: React.MouseEvent) => {
    if (!onHeaderDoubleTap) return;
    const target = e.target as HTMLElement;
    if (target.closest('.settings-section, button, a, input')) return;
    const now = Date.now();
    if (now - lastHeaderTapRef.current < 300) {
      onHeaderDoubleTap();
      lastHeaderTapRef.current = 0;
    } else {
      lastHeaderTapRef.current = now;
    }
  };

  const toggleFilters = (next: boolean) => {
    setShowFilters(next);
    onFiltersToggle?.(next);
  };

  const handleFilterSelection = () => {
    toggleFilters(false);
  };

  const isResultCountVisible = false;
  const baseLabels: Record<string, { asc?: string; desc?: string }> = {
    return: { desc: 'Hottest Slots', asc: 'Coldest Slots' },
    jackpot_value: { desc: 'Slots that hit Jackpots', asc: 'Slots that hit Jackpots' },
    money_played: { desc: 'Slots with the Most $ Played', asc: 'Slots with the Least $ Played' },
    money_won: { desc: 'Slots that Won the Most $', asc: 'Slots that Won the Least $' },
    spins: { desc: 'Most Popular Slots', asc: 'Least Popular Slots' },
    avg_money_won_per_spin: {
      desc: 'Slots with Highest Win/Spin',
      asc: 'Slots with Lowest Win/Spin',
    },
    non_jkpt_won_per_spin: {
      desc: 'Highest Win/Spin without Jackpots',
      asc: 'Lowest Win/Spin without Jackpots',
    },
    jackpot_value_per_spin: {
      desc: 'Slots with Highest Jackpot Win/Spin',
      asc: 'Slot with Lowest Jackpot Win/Spin',
    },
  };

  const groupLabels: Record<string, { asc?: string; desc?: string }> = groupName
    ? {
        return: { desc: `Hottest ${groupName}`, asc: `Coldest ${groupName}` },
        jackpots: {
          desc: `${groupPlural} that hit Jackpots`,
          asc: `${groupPlural} that hit Jackpots`,
        },
        money_played: {
          desc: `${groupName} with Most $ Played`,
          asc: `${groupName} with Least $ Played`,
        },
        money_won: {
          desc: `${groupPlural} that Won the Most`,
          asc: `${groupPlural} that Won the Least`,
        },
        spins: { desc: `Most Popular ${groupName}`, asc: `Least Popular ${groupName}` },
        avg_money_won_per_spin: {
          desc: `Highest Win/Spin ${groupPlural}`,
          asc: `Lowest Win/Spin ${groupPlural}`,
        },
        non_jkpt_won_per_spin: {
          desc: `Highest W/Spin-J ${groupPlural}`,
          asc: `Lowest W/Spin-J ${groupPlural}`,
        },
        jackpot_value_per_spin: {
          desc: `Highest JW/S ${groupPlural}`,
          asc: `Lowest JW/S ${groupPlural}`,
        },
      }
    : {};

  const labelLookup = groupName ? groupLabels : baseLabels;
  const friendlyName =
    labelLookup[insightKey]?.[orderLabel] || baseLabels[insightKey]?.[orderLabel] || 'Results';
  return (
    <IonToolbar className="results-toolbar" data-timeframe={timeframeKey} onClick={handleHeaderTap}>
      {/* Loading collapse wrapper (grid-template-rows 1fr <-> 0fr, same technique as the
          scroll collapse below): the list under the toolbar grows into the freed space,
          so the skeleton cards move up instead of leaving a blank band. */}
      <div
        className={`results-toolbar__collapse${
          sectionsCollapsed ? ' results-toolbar__collapse--collapsed' : ''
        }`}
        data-collapsed={sectionsCollapsed ? 'true' : 'false'}
        aria-hidden={sectionsCollapsed}
      >
        <div className="results-toolbar__collapse-inner">
          <div
            className={`results-toolbar__container results-toolbar__chrome${
              headerChromeVisible
                ? ' results-toolbar__chrome--visible'
                : ' results-toolbar__chrome--hidden'
            }`}
            aria-hidden={!headerChromeVisible}
          >
            <IonButtons slot="start" class="results-toolbar__title">
              <div className="results-toolbar__title-block">
                <span className="result-content">
                  {isResultCountVisible ? `${friendlyName} (${totalReportCount})` : friendlyName}
                </span>
                {/* {insightTimeframeLabel && <span className="group_name">{insightTimeframeLabel}</span>} */}
                <span className="group_name">{isBankFilter ? 'Bank ' + gameGroupName : ''}</span>
                <span className="group_name">
                  {!isBankFilter && !isSearchMode ? groupGameName : ''}
                </span>
                {fromSearchSubtitle ? (
                  <span className="group_name results-toolbar__from-search">{fromSearchSubtitle}</span>
                ) : null}
              </div>
            </IonButtons>
            {/* <IonButtons slot="end">
            <IonIcon icon={gitBranchOutline} style={iconStyle} />
            <IonIcon icon={bookmarkOutline} style={iconStyle} />
            <IonIcon icon={heartOutline} style={iconStyle} />
            <IonIcon icon={playCircleOutline} style={iconStyle} />
          </IonButtons> */}
            <div
              className={
                filtersVisible
                  ? 'results-toolbar__filters'
                  : 'results-toolbar__filters results-toolbar__filters--collapsed'
              }
              aria-hidden={!filtersVisible}
            >
              {/* Inner wrapper required for grid-template-rows 0fr/1fr collapse
                  (overflow:hidden + min-height:0 live here so the outer row can
                  animate without a max-height loop fighting ResizeObserver). */}
              <div className="results-toolbar__filters-inner">
                {/* <div className="results-toolbar__filters-header">
                  <div className="results-toolbar__filters-left">
                    <button
                      onClick={() => toggleFilters(!showFilters)}
                      className="results-toolbar__icon-button"
                      aria-label={showFilters ? 'Hide filters' : 'Show filters'}
                    >
                      <IonIcon icon={optionsOutline} className="results-toolbar__icon" />
                    </button>
                    {insightLabel && (
                      <span
                        className="results-toolbar__chip"
                        onClick={() => toggleFilters(!showFilters)}
                        style={{ cursor: 'pointer' }}
                      >
                        {insightLabel}
                      </span>
                    )}
                    {timeframeLabel && (
                      <span
                        className="results-toolbar__chip"
                        onClick={() => toggleFilters(!showFilters)}
                        style={{ cursor: 'pointer' }}
                      >
                        {timeframeLabel}
                      </span>
                    )}
                  </div>
                  {showFilters && (
                    <button
                      onClick={() => toggleFilters(false)}
                      className="results-toolbar__close-button"
                      aria-label="Hide filters"
                    >
                      <IonIcon icon={closeOutline} className="results-toolbar__icon" />
                    </button>
                  )}
                </div> */}

                <InsightInline selectedInsightValue={insightKey} onSelect={handleFilterSelection} />
                <TimeFrameInline
                  selectedTimeframeValue={timeframeKey}
                  selectedInsightValue={insightKey}
                  onSelect={handleFilterSelection}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </IonToolbar>
  );
};

export default ResultsToolbar;
